import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { getQuestionnaire, questionnaireActions, selectQuestionnaire, selectQuestionnaireState } from '../../store/entities/questionnaire';
import { SubmitButton } from '../common/form/SubmitButton';
import { Input } from '../common/form/Input';
import { questionnaireListActions } from '../../store/entities/questionnaireList';
import { Select } from '../common/form/Select';
import { QcsMenuItem } from '../common/basic/QcsMenuItem';
import { DetailHeader } from '../common/DetailHeader';
import { FormContainer } from '../common/form/FormContainer';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { TrainingAutocomplete } from '../common/form/TrainingAutocomplete';
import { FormGroup } from '../common/form/FormGroup';
import { DatePicker } from '../common/form/DatePicker';
import { QuestionnaireVersion } from './QuestionnaireVersion';
import { getDateForApi } from '../../utils/date';
import { fixNameInitValue, getLangNameObj } from '../../utils/format';
import { InputTextWithLang } from '../common/form/InputTextWithLang';
import { useNavigation } from '../../utils/urlPaths';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { EntityState } from '@api/models/enums/EntityState';
import { CreateQuestionnaireRequest } from '@api/models/questionnaire/CreateQuestionnaireRequest';
import { ShowError } from '../ShowError';
import { resources } from '@api/resources';
import { ShowResourceContent } from '../ShowResourceContent';
import { questionnaireService } from '@api/services/services';

type Validator = {
  name?: Yup.StringSchema<string, Yup.AnyObject, undefined, ''>;
  nameEn?: Yup.StringSchema<string, Yup.AnyObject, undefined, ''>;
  nameRu?: Yup.StringSchema<string, Yup.AnyObject, undefined, ''>;
  nameDe?: Yup.StringSchema<string, Yup.AnyObject, undefined, ''>;
  namePl?: Yup.StringSchema<string, Yup.AnyObject, undefined, ''>;
  validFrom?: Yup.StringSchema<string, Yup.AnyObject, undefined, ''>;
  minSuccessRate?: Yup.NumberSchema<number, Yup.AnyObject, undefined, ''>;
};

export const QuestionnaireDetail: FC = () => {
  const { t, i18n } = useTranslation();
  const { goBack, getBackPath } = useNavigation('/questionnaire');
  const { questionnaireId } = useParams();
  const questionnaire = useAppSelector(selectQuestionnaire);
  const dispatch = useAppDispatch();
  const companyCustomization = useAppSelector(selectCompanyCustomization);

  useEffect(() => {
    if (questionnaireId === 'new') {
      dispatch(questionnaireActions.default());
    } else {
      dispatch(getQuestionnaire(questionnaireId!));
    }
  }, [questionnaireId, dispatch]);

  const handleSubmit = async (data: CreateQuestionnaireRequest) => {
    if (questionnaireId === 'new') {
      await questionnaireService.create(data);
    } else if (questionnaireId) {
      await questionnaireService.update(questionnaireId, data);
    }

    dispatch(questionnaireListActions.reload(true));
    goBack();
  };

  const validator: Validator = {};

  if (companyCustomization?.applicationLanguages.includes('cs')) {
    validator['name'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('en')) {
    validator['nameEn'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('de')) {
    validator['nameDe'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('pl')) {
    validator['namePl'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('ru')) {
    validator['nameRu'] = validations.stringRequired(t);
  }
  if (questionnaireId === 'new') {
    validator['validFrom'] = validations.stringDateNotPastRequired(t);
    validator['minSuccessRate'] = validations.intRequiredMinMax(1, 100, t);
  }

  return (
    <>
      <DetailHeader title={getLangNameObj(i18n, questionnaire)} backTo={getBackPath} />
      <ShowResourceContent resource={resources.updateQuestionnaire} fetchStateSelector={selectQuestionnaireState}>
        <FormikDebounce<CreateQuestionnaireRequest>
          initialValues={{
            ...questionnaire,
            ...fixNameInitValue(i18n, questionnaire),
            validFrom: getDateForApi(new Date())!,
            minSuccessRate: 0,
          }}
          validationSchema={Yup.object().shape(validator)}
          onSubmit={handleSubmit}
        >
          <Form>
            <FormContainer>
              <InputTextWithLang isNew={questionnaireId === 'new'} label={t('questionnaire.name')} maxLength={50} required />

              <Select name="state" label={t('questionnaire.state')}>
                <QcsMenuItem value={EntityState.Active}>{t('entityState.ACTIVE')}</QcsMenuItem>
                <QcsMenuItem value={EntityState.Inactive}>{t('entityState.INACTIVE')}</QcsMenuItem>
              </Select>

              {questionnaireId !== 'new' && <TrainingAutocomplete multiple name="trainings" label={t('questionnaire.trainings')} disabled />}

              <Input name="note" label={t('questionnaire.note')} maxLength={250} multiline={true} rows={4} />

              {questionnaireId === 'new' && (
                <FormGroup name="questionnaire.firstVersion">
                  <DatePicker name="validFrom" label={t('questionnaire.validFrom')} disablePast disabled required />
                  <DatePicker name="validTo" label={t('questionnaire.validTo')} disabled />
                  <Input name="minSuccessRate" label={t('questionnaire.minSuccessRate')} type="number" />
                </FormGroup>
              )}

              <ShowError resource={[resources.updateQuestionnaire, resources.createQuestionnaire]} />
              <SubmitButton />
            </FormContainer>
          </Form>
        </FormikDebounce>
      </ShowResourceContent>
      {questionnaireId !== 'new' && <QuestionnaireVersion />}
    </>
  );
};
